import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';

import api from '../utils/api';
import {
  assignClientNotificationTemplate,
  unassignClientNotificationTemplate,
  saveNotificationTemplate,
  removeNotificationTemplate,
} from '../utils/api/templates';
import {
  channelLabel,
  notificationTemplateCatalog,
  validateNotificationTemplate,
} from '../utils/defaultNotificationTemplates';
import {
  buildTemplatePreviewContext,
  previewFrameDocument,
  renderNotificationTemplate,
  templateFrameDocument,
} from '../utils/templatePreview';

import Button from './common/Button';

import css from './NotificationTemplates.css';

const exampleText = (source, context) => renderNotificationTemplate(source, context);

const toggleDocumentFullscreen = (targetDocument) => {
  const doc = targetDocument || document;
  if (doc.fullscreenElement) doc.exitFullscreen();
  else doc.documentElement.requestFullscreen();
};

const openHtmlWindow = ({ title, html, onChange }) => {
  const popup = window.open('', 'elocker-template-html', 'width=1200,height=860');
  if (!popup) return;
  const safeTitle = String(title || 'Editar HTML').replace(/[<>]/g, '');
  popup.document.open();
  popup.document.write(
    `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${safeTitle}</title><style>html,body{margin:0;height:100%;background:#fff;color:#1c2833;font:14px/1.4 Helvetica,Arial,sans-serif}.bar{display:flex;justify-content:space-between;align-items:center;gap:12px;padding:8px 12px;border-bottom:1px solid #d5dbe1}.bar button{padding:4px 8px;font-weight:700;cursor:pointer}textarea{display:block;width:100%;height:calc(100% - 41px);border:0;padding:16px;box-sizing:border-box;resize:none;font:13px/1.45 ui-monospace,SFMono-Regular,Menlo,Consolas,monospace}</style></head><body><div class="bar"><strong>${safeTitle}</strong><button type="button" id="fs">Pantalla completa</button></div><textarea id="html" spellcheck="false"></textarea></body></html>`
  );
  popup.document.close();
  const area = popup.document.getElementById('html');
  area.value = html || '';
  area.addEventListener('input', () => onChange(area.value));
  popup.document.getElementById('fs').addEventListener('click', () => toggleDocumentFullscreen(popup.document));
  area.focus();
};

const openPreviewWindow = ({ title, documentHtml }) => {
  const popup = window.open('', 'elocker-template-preview', 'width=1200,height=860');
  if (!popup) return;
  const safeTitle = String(title || 'Vista previa HTML').replace(/[<>]/g, '');
  popup.document.open();
  popup.document.write(
    `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${safeTitle}</title><style>html,body{margin:0;height:100%;background:#eef1f4}.bar{display:flex;justify-content:space-between;align-items:center;gap:12px;padding:8px 12px;background:#fff;border-bottom:1px solid #d5dbe1;font:14px/1.4 Helvetica,Arial,sans-serif}.bar button{padding:4px 8px;font-weight:700;cursor:pointer}iframe{display:block;width:100%;height:calc(100% - 41px);border:0;background:#fff}</style></head><body><div class="bar"><strong>${safeTitle}</strong><button type="button" id="fs">Pantalla completa</button></div><iframe id="mail" sandbox="" referrerpolicy="no-referrer"></iframe></body></html>`
  );
  popup.document.close();
  popup.document.getElementById('mail').srcdoc = documentHtml;
  popup.document.getElementById('fs').addEventListener('click', () => toggleDocumentFullscreen(popup.document));
};

const PaneActions = ({ onFullscreen, onWindow }) => (
  <span className={css.paneActions}>
    <button type="button" onClick={onFullscreen}>
      Pantalla completa
    </button>
    <button type="button" onClick={onWindow}>
      Ventana
    </button>
  </span>
);

const BrowserFullscreen = ({ title, onClose, onWindow, children }) => {
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className={css.browserFullscreen} role="dialog" aria-label={title}>
      <div className={css.fullscreenBar}>
        <strong>{title}</strong>
        <span className={css.paneActions}>
          {onWindow && (
            <button type="button" onClick={onWindow}>
              Ventana
            </button>
          )}
          <button type="button" onClick={onClose}>
            Cerrar
          </button>
        </span>
      </div>
      <div className={css.fullscreenBody}>{children}</div>
    </div>
  );
};

const insertTokens = [
  ['{{order.pickupName}}', 'Destinatario'],
  ['{{order.pickupCode}}', 'Código de retiro'],
  ['{{order.pickupEmail}}', 'Email destinatario'],
  ['{{order.extras.idOrden}}', 'ID orden'],
  ['{{locker.name}}', 'Locker'],
  ['{{location.name}}', 'Ubicación'],
  ['{{location.address}}', 'Dirección'],
  ['{{door.doorNumber}}', 'Puerta'],
  ['{{client.name}}', 'Cliente'],
  ['{{client.supportEmail}}', 'Email soporte'],
  ['{{client.supportPhone}}', 'Teléfono soporte'],
  ['{{{formatDate order.autoReversalAt}}}', 'Fecha de revocación'],
];

const templateClientId = (template) => {
  if (!template) return null;
  if (template.clientId != null) return template.clientId;
  if (template.client_id != null) return template.client_id;
  if (template.client && template.client.id != null) return template.client.id;
  return null;
};

const templateRows = (index, clientId) => {
  if (!index.loaded || !index.data || !index.data.toJS) return [];
  const data = index.data.toJS();
  if (!Array.isArray(data)) return [];
  return data.filter((template) => String(templateClientId(template)) === String(clientId));
};

const isActive = (template) => {
  if (!template) return true;
  if (template.status === 'disabled' || template.status === 'inactive') return false;
  if (template.status === 'enabled' || template.status === 'active') return true;
  if (template.active != null) return !!template.active;
  if (template.enabled != null) return !!template.enabled;
  return true;
};

const apiError = (err, fallback) => {
  const data = err && err.response && err.response.data;
  return (data && (data.message || data.error)) || fallback;
};

const ChannelPreview = ({ title, children }) => (
  <section className={css.channelPreview}>
    <h3>{title}</h3>
    <div className={css.channelBody}>{children}</div>
  </section>
);

const templateRecordId = (template) => {
  if (!template) return null;
  if (template.id != null) return template.id;
  if (template.templateId != null) return template.templateId;
  if (template.template_id != null) return template.template_id;
  return null;
};

const templateType = (template) => (template && (template.type || template.notificationType)) || null;

const preferenceTemplateId = (preferences, type) => {
  const value = preferences[`${type}Template`];
  if (value == null || value === '' || typeof value === 'object') return null;
  return value;
};

const assignedTemplateRows = (preferences, rows) => {
  const prefs = preferences || {};
  return Object.entries(prefs)
    .filter(([key, value]) => key.endsWith('Template') && value != null && value !== '' && typeof value !== 'object')
    .map(([key, value]) => {
      const type = key.slice(0, -'Template'.length);
      const entry = notificationTemplateCatalog.find((item) => item.type === type);
      const template = rows.find((row) => String(templateRecordId(row)) === String(value));
      return {
        key,
        label: (entry && entry.label) || key,
        id: value,
      };
    })
    .sort((a, b) => String(a.label).localeCompare(String(b.label), 'es'));
};

const unassignedTemplateRows = (rows, assigned) => {
  const assignedIds = new Set(assigned.map((item) => String(item.id)));
  return rows
    .filter((row) => !assignedIds.has(String(templateRecordId(row))))
    .slice()
    .sort((a, b) => Number(templateRecordId(a)) - Number(templateRecordId(b)));
};


const customForType = (rows, preferences, type) => {
  const prefId = preferenceTemplateId(preferences, type);
  if (prefId != null) {
    const linked = rows.find((row) => String(row.id) === String(prefId));
    if (linked) return linked;
  }
  const typed = rows.filter((row) => templateType(row) === type);
  if (!typed.length) return null;
  return typed.slice().sort((a, b) => Number(b.id) - Number(a.id))[0];
};

const notificationInactive = (entry, preferences, notificationOn, custom) => {
  if (custom && !isActive(custom)) return true;
  if (!entry.channels) return false;
  if (entry.group === 'Devolución' && !preferences.returnsEnabled) return true;
  return !notificationOn;
};

const shownFields = (entry, custom) => {
  const source = custom || entry.defaults || {};
  return {
    emailSubject: source.emailSubject || '',
    emailTemplate: source.emailTemplate || '',
    smsTemplate: source.smsTemplate || '',
  };
};

const channelStates = (entry, preferences) => {
  if (!entry.channels) return [];
  return Object.entries(entry.channels).map(([channel, key]) => ({
    channel,
    key,
    label: channelLabel[channel] || channel,
    enabled: !!preferences[key],
  }));
};

const catalogEntryForTemplate = (template, preferences) => {
  const id = String(templateRecordId(template));
  const prefKey = Object.keys(preferences || {}).find(
    (key) => key.endsWith('Template') && String(preferences[key]) === id
  );
  const type = templateType(template) || (prefKey ? prefKey.slice(0, -'Template'.length) : null);
  const entry = type && notificationTemplateCatalog.find((item) => item.type === type);
  if (entry) return entry;
  return {
    type: type || 'custom',
    label: `Plantilla ${id}`,
    defaults: {
      emailSubject: (template && template.emailSubject) || '',
      emailTemplate: (template && template.emailTemplate) || '',
      smsTemplate: (template && template.smsTemplate) || '',
    },
    channels: null,
  };
};

const TemplatePreview = ({ label, subject, emailTemplate, smsTemplate, previewContext }) => {
  const [fullscreen, setFullscreen] = useState(false);
  const previewDoc = emailTemplate ? previewFrameDocument(emailTemplate, previewContext) : '';
  const emailFrame = emailTemplate ? (
    <iframe
      className={css.templateFrame}
      title={`Vista previa ${label}`}
      sandbox=""
      referrerPolicy="no-referrer"
      srcDoc={previewDoc}
    />
  ) : null;
  return (
    <div className={css.templateBlock}>
      <p className={css.note}>Vista con datos de ejemplo. El cliente actual reemplaza los datos de cliente.</p>
      <div className={css.channelPreviews}>
        {(subject || emailTemplate) && (
          <ChannelPreview title="Email">
            <div className={css.paneHead}>
              <span className={css.subjectCaption}>HTML</span>
              {emailTemplate && (
                <PaneActions
                  onFullscreen={() => setFullscreen(true)}
                  onWindow={() => openPreviewWindow({ title: `Vista previa ${label}`, documentHtml: previewDoc })}
                />
              )}
            </div>
            {subject && (
              <p className={css.subjectLine}>
                <span className={css.subjectCaption}>Asunto</span>
                {exampleText(subject, previewContext)}
              </p>
            )}
            {fullscreen ? (
              <BrowserFullscreen
                title={`Vista previa ${label}`}
                onClose={() => setFullscreen(false)}
                onWindow={() => openPreviewWindow({ title: `Vista previa ${label}`, documentHtml: previewDoc })}>
                {emailFrame}
              </BrowserFullscreen>
            ) : (
              emailFrame
            )}
          </ChannelPreview>
        )}
        {smsTemplate && (
          <ChannelPreview title="SMS">
            <p className={css.messageBody}>{exampleText(smsTemplate, previewContext)}</p>
          </ChannelPreview>
        )}
      </div>
    </div>
  );
};

const preferenceKeyFor = (type) => `${type}Template`;

const freeAssignmentOptions = (preferences) =>
  notificationTemplateCatalog
    .filter((entry) => preferenceTemplateId(preferences, entry.type) == null)
    .map((entry) => ({ key: preferenceKeyFor(entry.type), label: entry.label }));

const savedTemplateId = (saved, fallback) => {
  if (saved && saved.id != null) return saved.id;
  if (saved && saved.data && saved.data.id != null) return saved.data.id;
  return fallback;
};

const EmailTemplateEditor = ({
  template,
  seed,
  catalogEntry,
  preferences,
  client,
  clientId,
  onCancel,
  onSaved,
  reloadClient,
}) => {
  const editorFrameRef = useRef(null);
  const isNew = !template || !template.id;
  const initial = isNew ? seed || {} : template;
  const visualSnapshot = useRef(initial.emailTemplate || '');
  const [visualKey, setVisualKey] = useState(0);
  const [emailSubject, setEmailSubject] = useState(initial.emailSubject || '');
  const [smsTemplate, setSmsTemplate] = useState(initial.smsTemplate || '');
  const [html, setHtml] = useState(initial.emailTemplate || '');
  const [source, setSource] = useState(true);
  const [showPreview, setShowPreview] = useState(true);
  const [htmlFullscreen, setHtmlFullscreen] = useState(false);
  const [previewFullscreen, setPreviewFullscreen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const currentAssignments = isNew
    ? []
    : assignedTemplateRows(preferences, [template]).filter(
        (item) => String(item.id) === String(templateRecordId(template))
      );
  const assignmentLocked = isNew || currentAssignments.length > 0;
  const lockedAssignment = isNew
    ? { key: preferenceKeyFor(catalogEntry.type), label: catalogEntry.label }
    : currentAssignments[0];
  const freeOptions = freeAssignmentOptions(preferences);
  const [assignmentKey, setAssignmentKey] = useState(assignmentLocked && lockedAssignment ? lockedAssignment.key : '');
  const previewContext = useMemo(() => buildTemplatePreviewContext(client), [client]);

  const readEditorFrame = () => {
    const doc = editorFrameRef.current && editorFrameRef.current.contentDocument;
    if (!doc || !doc.body) return html;
    const headStyles = Array.from(doc.head ? doc.head.querySelectorAll('style:not([data-frame-chrome])') : [])
      .map((node) => node.outerHTML)
      .join('');
    return `${headStyles}${doc.body.innerHTML}`;
  };

  const currentHtml = () => (source || !editorFrameRef.current ? html : readEditorFrame());

  const onEditorLoad = () => {
    const doc = editorFrameRef.current && editorFrameRef.current.contentDocument;
    if (!doc || !doc.body) return;
    doc.body.contentEditable = 'true';
    doc.body.spellcheck = false;
    doc.body.oninput = () => setHtml(doc.body.innerHTML);
  };

  const format = (command, value) => {
    if (source) return;
    const doc = editorFrameRef.current && editorFrameRef.current.contentDocument;
    if (!doc) return;
    if (doc.body) doc.body.focus();
    doc.execCommand(command, false, value);
    if (doc.body) setHtml(doc.body.innerHTML);
  };

  const insertToken = (token) => {
    if (!token) return;
    if (source) {
      setHtml(`${html}${token}`);
      return;
    }
    format('insertHTML', token);
  };

  const draftErrors = useMemo(
    () =>
      validateNotificationTemplate({
        emailSubject,
        emailTemplate: html,
        smsTemplate,
        defaults: catalogEntry.defaults,
        channels: catalogEntry.channels,
        preferences,
      }),
    [emailSubject, html, smsTemplate, catalogEntry, preferences]
  );

  const onSave = async () => {
    const emailTemplate = currentHtml();
    const nextErrors = validateNotificationTemplate({
      emailSubject,
      emailTemplate,
      smsTemplate,
      defaults: catalogEntry.defaults,
      channels: catalogEntry.channels,
      preferences,
    });
    setHtml(emailTemplate);
    if (nextErrors.length) {
      setError(nextErrors[0]);
      return;
    }
    setSaving(true);
    setError(null);
    const payload = {
      clientId: Number(clientId),
      emailSubject: emailSubject.trim(),
      emailTemplate,
      smsTemplate: smsTemplate.trim(),
      status: 'enabled',
    };
    try {
      const saved = await saveNotificationTemplate(payload, isNew ? null : template.id);
      const savedId = savedTemplateId(saved, isNew ? null : templateRecordId(template));
      const keyToAssign = assignmentLocked ? lockedAssignment && lockedAssignment.key : assignmentKey;
      const alreadyAssigned = keyToAssign && preferences && String(preferences[keyToAssign]) === String(savedId);
      if (keyToAssign && !alreadyAssigned) {
        if (!savedId) throw new Error('No se recibió el ID de la plantilla para asignarla.');
        await assignClientNotificationTemplate(clientId, client, keyToAssign, savedId);
        if (reloadClient) await reloadClient();
      }
      if (!mounted.current) return;
      onSaved();
    } catch (err) {
      if (!mounted.current) return;
      setError(apiError(err, (err && err.message) || 'No se pudo guardar la plantilla.'));
      setSaving(false);
    }
  };

  return (
    <div className={css.editor}>
      <h3>
        {isNew ? `Nueva plantilla: ${catalogEntry.label}` : `Editar plantilla ID ${templateRecordId(template)}`}
      </h3>
      <p className={css.note}>La predeterminada no se modifica. Esta plantilla la reemplaza para este cliente.</p>
      {(error || draftErrors.length > 0) && (
        <ul className={css.errorList}>
          {(draftErrors.length ? draftErrors : [error]).map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}
      <div className={css.fields}>
        <label className={`${css.field} ${css.assignmentField}`}>
          Asignación
          {assignmentLocked ? (
            <select value={lockedAssignment ? lockedAssignment.key : ''} disabled>
              <option value={lockedAssignment ? lockedAssignment.key : ''}>
                {lockedAssignment ? lockedAssignment.label : catalogEntry.label}
              </option>
            </select>
          ) : (
            <select value={assignmentKey} onChange={(e) => setAssignmentKey(e.target.value)}>
              <option value="">Sin asignar</option>
              {freeOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
          <span className={css.assignmentNote}>
            {isNew
              ? 'Esta asignación queda seleccionada y fija. Primero se crea la plantilla y después se actualizan las preferencias del cliente.'
              : assignmentLocked
                ? 'Esta plantilla ya está asignada. La asignación no se cambia.'
                : freeOptions.length
                  ? 'Solo se listan preferencias del cliente que todavía no tienen plantilla. Primero se guarda la plantilla y después se asigna en el cliente.'
                  : 'No quedan preferencias de plantilla sin asignar.'}
          </span>
        </label>
        <label className={css.field}>
          Asunto
          <input
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            placeholder="Tu pedido está listo, {{order.pickupName}}"
          />
        </label>
      </div>
      <div className={css.controls}>
        <button type="button" onClick={() => format('bold')}>
          Negrita
        </button>
        <button type="button" onClick={() => format('italic')}>
          Cursiva
        </button>
        <button type="button" onClick={() => format('underline')}>
          Subrayado
        </button>
        <button type="button" onClick={() => format('formatBlock', 'h2')}>
          Título
        </button>
        <button type="button" onClick={() => format('formatBlock', 'p')}>
          Párrafo
        </button>
        <button type="button" onClick={() => format('insertUnorderedList')}>
          Lista
        </button>
        <button
          type="button"
          onClick={() => {
            const url = window.prompt('URL del enlace');
            if (url) format('createLink', url);
          }}>
          Enlace
        </button>
        <button
          type="button"
          onClick={() => {
            const url = window.prompt('URL de la imagen');
            if (url) format('insertImage', url);
          }}>
          Imagen
        </button>
        <select className={css.tokenSelect} value="" onChange={(e) => insertToken(e.target.value)}>
          <option value="">Insertar variable</option>
          {insertTokens.map(([token, label]) => (
            <option key={token} value={token}>
              {label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => {
            if (!source) {
              setHtml(readEditorFrame());
              setSource(true);
              return;
            }
            visualSnapshot.current = html;
            setVisualKey((key) => key + 1);
            setSource(false);
          }}>
          {source ? 'Visual' : 'HTML'}
        </button>
        <button
          type="button"
          onClick={() => {
            setHtml(currentHtml());
            setShowPreview(!showPreview);
          }}>
          {showPreview ? 'Ocultar vista previa' : 'Vista previa HTML'}
        </button>
      </div>
      <div className={css.htmlWorkspace}>
        <div className={css.htmlPane}>
          <div className={css.paneHead}>
            <h3>{source ? 'HTML' : 'Editor visual'}</h3>
            <PaneActions
              onFullscreen={() => {
                const next = currentHtml();
                visualSnapshot.current = next;
                setHtml(next);
                setVisualKey((key) => key + 1);
                setHtmlFullscreen(true);
              }}
              onWindow={() => openHtmlWindow({ title: 'Editar HTML', html: currentHtml(), onChange: setHtml })}
            />
          </div>
          {htmlFullscreen ? (
            <BrowserFullscreen
              title={source ? 'Editar HTML' : 'Editor visual'}
              onClose={() => {
                const next = currentHtml();
                visualSnapshot.current = next;
                setHtml(next);
                setVisualKey((key) => key + 1);
                setHtmlFullscreen(false);
              }}
              onWindow={() => openHtmlWindow({ title: 'Editar HTML', html: currentHtml(), onChange: setHtml })}>
              {source ? (
                <textarea className={css.source} value={html} onChange={(e) => setHtml(e.target.value)} spellCheck={false} />
              ) : (
                <iframe
                  key={visualKey}
                  ref={editorFrameRef}
                  className={css.templateFrame}
                  title="Editor visual de la plantilla"
                  sandbox="allow-same-origin"
                  referrerPolicy="no-referrer"
                  srcDoc={templateFrameDocument(visualSnapshot.current)}
                  onLoad={onEditorLoad}
                />
              )}
            </BrowserFullscreen>
          ) : source ? (
            <textarea className={css.source} value={html} onChange={(e) => setHtml(e.target.value)} spellCheck={false} />
          ) : (
            <iframe
              key={visualKey}
              ref={editorFrameRef}
              className={css.templateFrame}
              title="Editor visual de la plantilla"
              sandbox="allow-same-origin"
              referrerPolicy="no-referrer"
              srcDoc={templateFrameDocument(visualSnapshot.current)}
              onLoad={onEditorLoad}
            />
          )}
        </div>
        {showPreview && (
          <div className={css.htmlPane}>
            <div className={css.paneHead}>
              <h3>Vista previa HTML</h3>
              <PaneActions
                onFullscreen={() => setPreviewFullscreen(true)}
                onWindow={() =>
                  openPreviewWindow({
                    title: 'Vista previa HTML',
                    documentHtml: previewFrameDocument(currentHtml(), previewContext),
                  })
                }
              />
            </div>
            {emailSubject && (
              <p className={css.subjectLine}>
                <span className={css.subjectCaption}>Asunto</span>
                {exampleText(emailSubject, previewContext)}
              </p>
            )}
            {previewFullscreen ? (
              <BrowserFullscreen
                title="Vista previa HTML"
                onClose={() => setPreviewFullscreen(false)}
                onWindow={() =>
                  openPreviewWindow({
                    title: 'Vista previa HTML',
                    documentHtml: previewFrameDocument(html, previewContext),
                  })
                }>
                <iframe
                  className={css.templateFrame}
                  title="Vista previa de la plantilla"
                  sandbox=""
                  referrerPolicy="no-referrer"
                  srcDoc={previewFrameDocument(html, previewContext)}
                />
              </BrowserFullscreen>
            ) : (
              <iframe
                className={css.templateFrame}
                title="Vista previa de la plantilla"
                sandbox=""
                referrerPolicy="no-referrer"
                srcDoc={previewFrameDocument(html, previewContext)}
              />
            )}
          </div>
        )}
      </div>
      <div className={css.fields}>
        <label className={css.field}>
          SMS
          <textarea value={smsTemplate} onChange={(e) => setSmsTemplate(e.target.value)} />
        </label>
      </div>
      {showPreview && smsTemplate && (
        <p className={css.messageBody}>{exampleText(smsTemplate, previewContext)}</p>
      )}
      <div className={css.actions}>
        <Button primary noMargin disabled={saving || draftErrors.length > 0} onClick={onSave}>
          {saving ? 'Guardando' : 'Guardar'}
        </Button>
        <Button noMargin disabled={saving} onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </div>
  );
};

const NotificationTemplates = ({ clientId, client, readOnly, reloadClient }) => {
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const params = useMemo(() => ({ clientId, perPage: 100 }), [clientId]);
  const { index, actions } = api.templates.index(params);
  const [editingType, setEditingType] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [customOpen, setCustomOpen] = useState({});
  const [expanded, setExpanded] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const rows = templateRows(index, clientId);
  const preferences =
    client && client.get && client.get('preferences') ? client.get('preferences').toJS() : {};
  const previewContext = useMemo(() => buildTemplatePreviewContext(client), [client]);

  const refresh = async () => {
    if (!mounted.current) return;
    setEditingType(null);
    setEditingId(null);
    setBusyId(null);
    await actions.get();
  };

  const onDelete = (template) => async () => {
    const confirmed = window.confirm('¿Estás seguro de eliminar esta plantilla? No la podrás recuperar.');
    if (!confirmed) return;
    setBusyId(template.id);
    setError(null);
    try {
      await removeNotificationTemplate(template.id);
      if (!mounted.current) return;
      if (editingType === templateType(template)) setEditingType(null);
      await actions.get();
    } catch (err) {
      if (!mounted.current) return;
      setError(apiError(err, 'No se pudo eliminar la plantilla personalizada.'));
    }
    if (mounted.current) setBusyId(null);
  };

  const onUnassign = (preferenceKey) => async () => {
    setBusyId(preferenceKey);
    setError(null);
    try {
      await unassignClientNotificationTemplate(clientId, client, preferenceKey);
      if (!mounted.current) return;
      if (reloadClient) await reloadClient();
      await actions.get();
    } catch (err) {
      if (!mounted.current) return;
      setError(apiError(err, 'No se pudo desasignar la plantilla.'));
    }
    if (mounted.current) setBusyId(null);
  };

  const onToggleStatus = (template) => async () => {
    const nextStatus = isActive(template) ? 'disabled' : 'enabled';
    setBusyId(template.id);
    setError(null);
    try {
      await saveNotificationTemplate({ status: nextStatus }, template.id);
      if (!mounted.current) return;
      await actions.get();
    } catch (err) {
      if (!mounted.current) return;
      setError(apiError(err, 'No se pudo cambiar el estado de la plantilla.'));
    }
    if (mounted.current) setBusyId(null);
  };

  let lastGroup = null;

  return (
    <Fragment>
      <div className={css.toolbar}>
        <div>
          <h2>Plantillas de notificación</h2>
          <p className={css.note}>
            Siempre se lista cada tipo. Todas las plantillas empiezan contraídas. La predeterminada no se edita:
            Nueva crea el reemplazo y Editar actualiza el existente. Cada plantilla personalizada tiene vista previa
            y un guardado que hace patch.
          </p>
        </div>
      </div>
      {error && <p className={css.error}>{error}</p>}
      {index.error && <p className={css.error}>{index.error}</p>}
      {!index.loaded && <p className={css.note}>Cargando plantillas personalizadas. Se muestran las predeterminadas.</p>}
       {index.loaded && unassignedTemplateRows(rows, assignedTemplateRows(preferences, rows)).length > 0 && (
        <div className={css.customList}>
          <h3 className={css.group}>Plantillas personalizadas</h3>
          {unassignedTemplateRows(rows, assignedTemplateRows(preferences, rows)).map((template) => {
              const id = templateRecordId(template);
              const editing = String(editingId) === String(id);
              const collapsed = customOpen[id] == null ? true : !customOpen[id];
              const assignments = assignedTemplateRows(preferences, [template]).filter(
                (item) => String(item.id) === String(id)
              );
              const catalogEntry = catalogEntryForTemplate(template, preferences);
              return (
                <article key={id} className={collapsed ? css.cardCollapsed : css.card}>
                  <div className={css.cardHead}>
                    <div>
                      <button
                        type="button"
                        className={css.collapseToggle}
                        aria-expanded={!collapsed}
                        onClick={() => setCustomOpen((current) => ({ ...current, [id]: collapsed }))}>
                        {collapsed ? <FiChevronDown /> : <FiChevronUp />}
                        <h3 className={css.name}>
                          {catalogEntry.label || `Plantilla ${id}`}
                          <span className={css.templateId}>ID {id}</span>
                        </h3>
                      </button>
                      <span className={assignments.length ? css.statusOn : css.statusOff}>
                        {assignments.length
                          ? `Asignada: ${assignments.map((item) => item.label).join(', ')}`
                          : 'No asignada'}
                      </span>
                    </div>
                    {!readOnly && (
                      <div className={css.cardActions}>
                        <Button
                          small
                          noMargin
                          blue={!editing}
                          onClick={() => {
                            setCustomOpen((current) => ({ ...current, [id]: true }));
                            setEditingId(editing ? null : id);
                          }}>
                          {editing ? 'Cerrar' : 'Editar'}
                        </Button>
                        <Button
                          small
                          noMargin
                          destructive
                          disabled={busyId === template.id}
                          onClick={onDelete(template)}>
                          Eliminar
                        </Button>
                      </div>
                    )}
                  </div>
                  {!collapsed && (editing && !readOnly ? (
                    <EmailTemplateEditor
                      template={template}
                      seed={template}
                      catalogEntry={catalogEntry}
                      preferences={preferences}
                      client={client}
                      clientId={clientId}
                      onCancel={() => setEditingId(null)}
                      reloadClient={reloadClient}
                      onSaved={refresh}
                    />
                  ) : (
                    <TemplatePreview
                      label={catalogEntry.label || `ID ${id}`}
                      subject={template.emailSubject}
                      emailTemplate={template.emailTemplate}
                      smsTemplate={template.smsTemplate}
                      previewContext={previewContext}
                    />
                  ))}
                </article>
              );
            })}
        </div>
      )}
      <div className={css.catalog}>
        {notificationTemplateCatalog
          .map((entry, position) => ({
            entry,
            position,
            notificationOn: channelStates(entry, preferences).some((channel) => channel.enabled),
          }))
          .sort((a, b) => Number(b.notificationOn) - Number(a.notificationOn) || a.position - b.position)
          .map(({ entry }) => {
          const custom = index.loaded ? customForType(rows, preferences, entry.type) : null;
          const shown = shownFields(entry, custom);
          const channels = channelStates(entry, preferences);
          const notificationOn = channels.some((channel) => channel.enabled);
          const inactive = notificationInactive(entry, preferences, notificationOn, custom);
          const open = editingType === entry.type;
          const collapsed = expanded[entry.type] == null ? true : !expanded[entry.type];
          const showGroup = entry.group !== lastGroup;
          lastGroup = entry.group;
          const extras = rows.filter(
            (row) => templateType(row) === entry.type && (!custom || String(row.id) !== String(custom.id))
          );
          const panelClass = [
            collapsed ? css.cardCollapsed : css.card,
            custom ? css.cardCustom : '',
            !inactive && notificationOn ? css.cardActive : '',
            !custom && inactive ? css.cardIdle : '',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <Fragment key={entry.type}>
              {showGroup && <h3 className={css.group}>{entry.group}</h3>}
              <article className={panelClass}>
                <div className={css.cardHead}>
                  <div>
                    <button
                      type="button"
                      className={css.collapseToggle}
                      aria-expanded={!collapsed}
                      onClick={() => setExpanded((current) => ({ ...current, [entry.type]: collapsed }))}>
                      {collapsed ? <FiChevronDown /> : <FiChevronUp />}
                      <h3 className={css.name}>
                        {entry.label}
                        {custom && <span className={css.templateId}>ID {templateRecordId(custom)}</span>}
                      </h3>
                    </button>
                    &nbsp;
                    <span className={custom ? css.statusOn : css.statusOff}>
                      {custom ? `Personalizada ID ${templateRecordId(custom)}` : 'Predeterminada'}
                    </span>
                    {custom && !isActive(custom) && <span className={css.usage}>Plantilla personalizada inactiva</span>}
                  </div>
                  {!readOnly && (
                    <div className={css.cardActions}>
                      <Button
                        small
                        noMargin
                        green={!custom && !open}
                        blue={!!custom && !open}
                        onClick={() => {
                          setExpanded((current) => ({ ...current, [entry.type]: true }));
                          setEditingType(open ? null : entry.type);
                        }}>
                        {open ? 'Cerrar' : custom ? 'Editar' : 'Nueva'}
                      </Button>
                      {custom && (
                        <Button
                          small
                          noMargin
                          green={!isActive(custom)}
                          disabled={busyId === custom.id}
                          onClick={onToggleStatus(custom)}>
                          {isActive(custom) ? 'Desactivar' : 'Activar'}
                        </Button>
                      )}
                      {preferenceTemplateId(preferences, entry.type) != null && (
                        <Button
                          small
                          noMargin
                          disabled={busyId === preferenceKeyFor(entry.type)}
                          onClick={onUnassign(preferenceKeyFor(entry.type))}>
                          Desasignar
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                {!collapsed &&
                  (entry.channels ? (
                    <div className={css.channels}>
                      <span className={notificationOn ? css.statusOn : css.statusOff}>
                        {notificationOn ? 'Notificación activada' : 'Notificación desactivada'}
                      </span>
                      {channels.map((channel) => (
                        <span key={channel.key} className={channel.enabled ? css.statusOn : css.statusOff}>
                          {channel.label}: {channel.enabled ? 'activado' : 'desactivado'}
                        </span>
                      ))}
                      {entry.group === 'Devolución' && !preferences.returnsEnabled && (
                        <span className={css.statusOff}>Devoluciones desactivadas</span>
                      )}
                    </div>
                  ) : (
                    <p className={css.usage}>Sin interruptor en la sección Notificaciones.</p>
                  ))}
                {!collapsed && extras.length > 0 && (
                  <p className={css.usage}>
                    Otras plantillas de este tipo: {extras.map((row) => `ID ${templateRecordId(row)}`).join(', ')}
                  </p>
                )}
                {!collapsed && (open && !readOnly ? (
                  <EmailTemplateEditor
                    template={custom}
                    seed={{ ...shown, type: entry.type }}
                    catalogEntry={entry}
                    preferences={preferences}
                    client={client}
                    reloadClient={reloadClient}
                    clientId={clientId}
                    onCancel={() => setEditingType(null)}
                    onSaved={refresh}
                  />
                ) : (
                  <TemplatePreview
                    label={entry.label}
                    subject={shown.emailSubject}
                    emailTemplate={shown.emailTemplate}
                    smsTemplate={shown.smsTemplate}
                    previewContext={previewContext}
                  />
                ))}
              </article>
            </Fragment>
          );
        })}
      </div>
    </Fragment>
  );
};

export default NotificationTemplates;
